// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CONSTRAINT_TYPE_LABELS,
  humanizeConstraint,
} from "@/lib/constraints/humanize";
import {
  Clock,
  Users,
  X,
  Plus,
  AlertCircle,
  Loader2,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────

interface Props {
  eventId: string;
  teamId: string;
  organizationId: string;
}

type ConstraintType =
  | "team_blackout"
  | "must_play_before"
  | "must_play_after"
  | "coach_conflict";

type ConstraintStatus =
  | "pending_review"
  | "approved"
  | "denied"
  | "partial"
  | "superseded";

interface ScheduleConstraint {
  id: string;
  constraint_type: ConstraintType;
  parameters: Record<string, any>;
  severity: string;
  status: ConstraintStatus;
  response_notes: string | null;
  partial_accommodation: string | null;
  created_at: string;
  reviewed_at: string | null;
}

interface OpponentCandidate {
  team_id: string;
  display_name: string;
  age_group: string | null;
  gender: string | null;
}

const COACH_CONSTRAINT_TYPES: ConstraintType[] = [
  "team_blackout",
  "must_play_before",
  "must_play_after",
  "coach_conflict",
];

const STATUS_STYLES: Record<
  ConstraintStatus,
  { label: string; className: string }
> = {
  pending_review: {
    label: "Pending review",
    className: "bg-yellow-100 text-yellow-800 border-yellow-200",
  },
  approved: {
    label: "Approved",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  partial: {
    label: "Partial accommodation",
    className: "bg-blue-100 text-blue-800 border-blue-200",
  },
  denied: {
    label: "Denied",
    className: "bg-red-100 text-red-800 border-red-200",
  },
  superseded: {
    label: "Superseded",
    className: "bg-gray-100 text-gray-600 border-gray-200",
  },
};

// ── Component ──────────────────────────────────────────────────────────

export function TeamEventScheduleRequests({
  eventId,
  teamId,
  organizationId,
}: Props) {
  const { profile } = useAuth();
  const [, setSearchParams] = useSearchParams();

  // ── Data state ──
  const [constraints, setConstraints] = useState<ScheduleConstraint[]>([]);
  const [opponents, setOpponents] = useState<OpponentCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ── Form state ──
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formType, setFormType] = useState<ConstraintType>("team_blackout");
  const [formDate, setFormDate] = useState("");
  const [formStartTime, setFormStartTime] = useState("");
  const [formEndTime, setFormEndTime] = useState("");
  const [formTime, setFormTime] = useState("");
  const [formRelatedTeamId, setFormRelatedTeamId] = useState("");

  // ── Empty-team guard ──
  if (!teamId) {
    return (
      <Card>
        <CardContent className="pt-6 flex flex-col items-center text-center gap-4">
          <Users className="h-12 w-12 text-gray-400" />
          <p className="text-sm text-muted-foreground">
            Select a team on the Roster tab first to submit schedule requests.
          </p>
          <Button
            variant="outline"
            onClick={() => setSearchParams({ tab: "roster" }, { replace: true })}
          >
            Go to Roster tab
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ── Data fetching ──

  const fetchConstraints = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("get_team_event_constraints", {
      p_event_id: eventId,
      p_team_id: teamId,
    });
    if (error) {
      console.error("Error fetching constraints:", error);
      toast.error("Failed to load schedule requests");
    } else {
      setConstraints((data as ScheduleConstraint[]) || []);
    }
    setLoading(false);
  }, [eventId, teamId]);

  const fetchOpponents = useCallback(async () => {
    const { data, error } = await supabase.rpc(
      "get_event_opponent_candidates",
      { p_event_id: eventId, p_exclude_team_id: teamId }
    );
    if (error) {
      console.error("Error fetching opponents:", error);
    } else {
      setOpponents((data as OpponentCandidate[]) || []);
    }
  }, [eventId, teamId]);

  useEffect(() => {
    fetchConstraints();
    fetchOpponents();
  }, [fetchConstraints, fetchOpponents]);

  // ── Derived data ──

  const opponentMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of opponents) {
      m.set(o.team_id, o.display_name);
    }
    return m;
  }, [opponents]);

  const opponentOptions = useMemo(
    () =>
      opponents.map((o) => ({
        value: o.team_id,
        label: `${o.display_name}${o.age_group || o.gender ? ` — ${[o.age_group, o.gender].filter(Boolean).join(" ")}` : ""}`,
      })),
    [opponents]
  );

  // ── Form helpers ──

  const resetForm = () => {
    setFormType("team_blackout");
    setFormDate("");
    setFormStartTime("");
    setFormEndTime("");
    setFormTime("");
    setFormRelatedTeamId("");
    setEditingId(null);
  };

  const openNewForm = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEditForm = (c: ScheduleConstraint) => {
    setEditingId(c.id);
    setFormType(c.constraint_type);
    const p = c.parameters;
    setFormDate(p.date || "");
    setFormStartTime(p.start_time || "");
    setFormEndTime(p.end_time || "");
    setFormTime(p.time || "");
    setFormRelatedTeamId(p.related_team_id || "");
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const validate = (): string | null => {
    if (formType === "team_blackout") {
      if (!formDate) return "Date is required.";
      if (!formStartTime || !formEndTime) return "Both start and end times are required.";
      if (formStartTime >= formEndTime) return "Start time must be before end time.";
    }
    if (formType === "must_play_before" || formType === "must_play_after") {
      if (!formDate) return "Date is required.";
      if (!formTime) return "Time is required.";
    }
    if (formType === "coach_conflict") {
      if (!formRelatedTeamId) return "Select a team.";
    }
    return null;
  };

  const buildParams = (): Record<string, any> => {
    if (formType === "team_blackout") {
      return { date: formDate, start_time: formStartTime, end_time: formEndTime };
    }
    if (formType === "must_play_before" || formType === "must_play_after") {
      return { date: formDate, time: formTime };
    }
    return { related_team_id: formRelatedTeamId };
  };

  // ── Submit ──

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setSaving(true);
    const params = buildParams();

    if (editingId) {
      const { error } = await supabase
        .from("schedule_constraints")
        .update({
          constraint_type: formType,
          parameters: params,
          severity: "hard",
        })
        .eq("id", editingId)
        .eq("status", "pending_review");

      if (error) {
        console.error("Error updating constraint:", error);
        toast.error("Failed to update request");
        setSaving(false);
        return;
      }
      toast.success("Request updated");
    } else {
      const { error } = await supabase.from("schedule_constraints").insert({
        organization_id: organizationId,
        event_id: eventId,
        team_id: teamId,
        source: "coach_request",
        constraint_type: formType,
        parameters: params,
        severity: "hard",
        status: "pending_review",
        created_by: profile?.id,
      });

      if (error) {
        console.error("Error creating constraint:", error);
        toast.error("Failed to submit request");
        setSaving(false);
        return;
      }
      toast.success("Request submitted");
    }

    setSaving(false);
    closeForm();
    fetchConstraints();
  };

  // ── Cancel (soft-delete) ──

  const handleCancelRequest = async (id: string) => {
    if (!window.confirm("Cancel this schedule request?")) return;

    const { error } = await supabase
      .from("schedule_constraints")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id)
      .eq("status", "pending_review");

    if (error) {
      console.error("Error cancelling constraint:", error);
      toast.error("Failed to cancel request");
      return;
    }
    toast.success("Request cancelled");
    fetchConstraints();
  };

  // ── Render ──

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Schedule Requests</h2>
          <p className="text-sm text-muted-foreground">
            Request constraints for how this team is scheduled. Approved
            requests are honored by the scheduling engine.
          </p>
        </div>
        {!formOpen && (
          <Button onClick={openNewForm} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New request
          </Button>
        )}
      </div>

      {/* Form */}
      {formOpen && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-semibold">
              {editingId ? "Edit request" : "New request"}
            </h3>

            {/* Type selector */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Constraint type</label>
              <Select
                value={formType}
                onValueChange={(v) => setFormType(v as ConstraintType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COACH_CONSTRAINT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {CONSTRAINT_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* team_blackout fields */}
            {formType === "team_blackout" && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Date</label>
                  <Input
                    type="date"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Start time
                    </label>
                    <Input
                      type="time"
                      value={formStartTime}
                      onChange={(e) => setFormStartTime(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium flex items-center gap-1">
                      <Clock className="h-3 w-3" /> End time
                    </label>
                    <Input
                      type="time"
                      value={formEndTime}
                      onChange={(e) => setFormEndTime(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* must_play_before / must_play_after fields */}
            {(formType === "must_play_before" ||
              formType === "must_play_after") && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Date</label>
                  <Input
                    type="date"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Time
                  </label>
                  <Input
                    type="time"
                    value={formTime}
                    onChange={(e) => setFormTime(e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {formType === "must_play_before"
                    ? "Your team's last game must end at or before this time"
                    : "Your team's first game must start at or after this time"}
                </p>
              </div>
            )}

            {/* coach_conflict fields */}
            {formType === "coach_conflict" && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium flex items-center gap-1">
                    <Users className="h-3 w-3" /> Other team with shared coach
                  </label>
                  <Select
                    value={formRelatedTeamId}
                    onValueChange={setFormRelatedTeamId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a team..." />
                    </SelectTrigger>
                    <SelectContent>
                      {opponentOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">
                  Your team and this team cannot play at the same time
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={closeForm} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Update request" : "Submit request"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">Loading requests...</span>
        </div>
      ) : constraints.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-muted-foreground">No requests yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {constraints.map((c) => {
            const style = STATUS_STYLES[c.status];
            return (
              <Card key={c.id}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">
                        {CONSTRAINT_TYPE_LABELS[c.constraint_type]}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {humanizeConstraint(c, {
                          opponentNameLookup: (id) => opponentMap.get(id),
                        })}
                      </p>
                      {c.status === "denied" && c.response_notes && (
                        <p className="text-sm text-red-600 mt-1 flex items-start gap-1">
                          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                          {c.response_notes}
                        </p>
                      )}
                      {/* TODO: partial_accommodation is jsonb; operator inbox may populate as object. Handle object shape when that lands. */}
                      {c.status === "partial" && c.partial_accommodation && (
                        <p className="text-sm text-blue-600 mt-1">
                          {c.partial_accommodation}
                        </p>
                      )}
                      {c.reviewed_at && c.status !== "pending_review" && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Reviewed{" "}
                          {new Date(c.reviewed_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={style.className}>
                        {style.label}
                      </Badge>
                      {c.status === "pending_review" && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditForm(c)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleCancelRequest(c.id)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}